/**
 * Silo V2 InterestRateModelV2 rate computation — stripped-down transplant
 * of the logic in `@1delta/irm-sdk/models/silo/kink-v2.ts`.
 *
 * Kept here so the margin-fetcher doesn't need to depend on the full
 * irm-sdk for a single rate call. The model shape is shared between
 * Silo v2 and v3 (v3 reuses the exact same IRM contract), so this helper
 * serves both parsers.
 */
import type { SiloHalfStatic } from '@1delta/data-sdk'

const DP = 1e18
const WAD = 1e18
const SECONDS_PER_YEAR = 365 * 24 * 3600

/** Raw IRM params (int256 values at 1e18 scale). */
export interface SiloIRMParams {
  uopt: bigint
  ucrit: bigint
  ulow: bigint
  klin: bigint
  ki: bigint
  kcrit: bigint
  klow: bigint
  beta: bigint
  ri: bigint
  Tcrit: bigint
  /** Decimal fraction (0..1), optional. */
  daoFee?: number
  /** Decimal fraction (0..1), optional. */
  deployerFee?: number
}

function toBigIntSafe(val: any): bigint {
  if (typeof val === 'bigint') return val
  if (val == null || val === '0x') return 0n
  try {
    return BigInt(val as any)
  } catch {
    return 0n
  }
}

/**
 * Parse the IRM.getConfig(silo) tuple into `SiloIRMParams`.
 * Returns null when the call reverted or returned an unexpected shape.
 */
export function parseIRMConfig(
  raw: any,
  half: SiloHalfStatic,
): SiloIRMParams | null {
  if (!raw || raw === '0x') return null
  try {
    const uopt = toBigIntSafe(raw.uopt ?? raw[0])
    const ucrit = toBigIntSafe(raw.ucrit ?? raw[1])
    const ulow = toBigIntSafe(raw.ulow ?? raw[2])
    const klin = toBigIntSafe(raw.klin ?? raw[3])
    const ki = toBigIntSafe(raw.ki ?? raw[4])
    const kcrit = toBigIntSafe(raw.kcrit ?? raw[5])
    const klow = toBigIntSafe(raw.klow ?? raw[6])
    const beta = toBigIntSafe(raw.beta ?? raw[7])
    const ri = toBigIntSafe(raw.ri ?? raw[8])
    const Tcrit = toBigIntSafe(raw.Tcrit ?? raw[9])

    const daoFee =
      half.daoFee != null ? Number(BigInt(half.daoFee)) / WAD : undefined
    const deployerFee =
      half.deployerFee != null
        ? Number(BigInt(half.deployerFee)) / WAD
        : undefined

    return {
      uopt,
      ucrit,
      ulow,
      klin,
      ki,
      kcrit,
      klow,
      beta,
      ri,
      Tcrit,
      ...(daoFee != null ? { daoFee } : {}),
      ...(deployerFee != null ? { deployerFee } : {}),
    }
  } catch {
    return null
  }
}

/**
 * Instantaneous borrow rate (APR %) at a given utilization. Matches the
 * on-chain `calculateCurrentInterestRate` with T=0.
 */
function calculateBorrowRate(
  utilization: number,
  params: SiloIRMParams,
): number {
  const uopt = Number(params.uopt) / DP
  const ucrit = Number(params.ucrit) / DP
  const ulow = Number(params.ulow) / DP
  const klin = Number(params.klin) / DP
  const ki = Number(params.ki) / DP
  const kcrit = Number(params.kcrit) / DP
  const klow = Number(params.klow) / DP
  const ri = Number(params.ri) / DP
  const Tcrit = Number(params.Tcrit) / DP
  const beta = Number(params.beta) / DP

  const u = utilization
  const T = 0

  // rp: penalty above ucrit, bonus below ulow
  const rp =
    u > ucrit
      ? kcrit * (1 + Tcrit + beta * T) * (u - ucrit)
      : Math.min(0, klow * (u - ulow))

  const rlin = klin * u
  let riEff = Math.max(ri, rlin)
  riEff = Math.max(riEff + ki * (u - uopt) * T, rlin)
  const rcurPerSecond = Math.max(riEff + rp, rlin)
  const rcurAnnual = rcurPerSecond * SECONDS_PER_YEAR

  // Cap at 10,000% APR, floor at 0
  return Math.min(Math.max(rcurAnnual * 100, 0), 10000)
}

/** Borrow + deposit rates (APR %) given the IRM state and utilization. */
export function calculateSiloRates(
  utilization: number,
  params: SiloIRMParams,
): { borrowRate: number; depositRate: number } {
  const borrowRate = calculateBorrowRate(utilization, params)
  const daoFee = params.daoFee ?? 0
  const deployerFee = params.deployerFee ?? 0
  const totalFee = daoFee + deployerFee
  const depositRate =
    (borrowRate / 100) * utilization * (1 - totalFee) * 100
  return { borrowRate, depositRate }
}
